import React, { useEffect, useState } from 'react'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import './DonationGraph.scss'

export default ({ donorData }) => {
  const { donationHBUnit } = useSelector((state) => state.app.appSettings)
  const [key, setKey] = useState(0)
  const {
    user: { gender },
    appSettings: { dateFormat, thresholdsHBDonationGraph },
  } = useSelector((state) => state.app)

  const eligibleToDonateBlood = thresholdsHBDonationGraph.eligibleToDonateBlood[gender]
    ? thresholdsHBDonationGraph.eligibleToDonateBlood[gender]
    : 135
  const promptWarningIfBelow = thresholdsHBDonationGraph.promptWarningIfBelow[gender]
    ? thresholdsHBDonationGraph.promptWarningIfBelow[gender]
    : 125

  const [chartOptions, setChartOptions] = useState({
    title: false,
    timezone: 'Europe/Stockholm',
    chart: {
      backgroundColor: '#E2E2E2',
    },
    credits: {
      enabled: false,
    },
    series: [],
    yAxis: {
      min: 100,
      max: 160,
      title: {
        text: donationHBUnit || 'g/l',
      },
      plotLines: [
        {
          value: eligibleToDonateBlood,
          color: '#FA785A',
          dashStyle: 'longdash',
          width: 2,
          label: {
            text: __.trans('donation.graph-too-low-for-donation', { eligibleToDonateBlood }),
            style: {
              color: '#FA785A',
              fontSize: '10px',
            },
            y: 16,
          },
        },
        {
          value: promptWarningIfBelow,
          color: '#AF0A3C',
          dashStyle: 'longdash',
          width: 2,
          label: {
            text: __.trans('donation.graph-under-normal', { promptWarningIfBelow }),
            style: {
              color: '#AF0A3C',
              fontSize: '10px',
            },
            y: 16,
          },
        },
      ],
    },
    xAxis: [
      {
        type: 'datetime',
        labels: {
          useHTML: true,
          formatter: ({ value }) => Highcharts.dateFormat('%b -%y', new Date(value)),
        },
      },
    ],
    legend: [
      {
        enabled: false,
      },
    ],
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 650,
          },
          chartOptions: {
            series: {
              lineWidth: 1,
              marker: {
                radius: 4,
              },
            },
            yAxis: {
              title: {
                text: null,
              },
            },
          },
        },
      ],
    },
  })

  // re-render graph on rotation/resize
  const handleRotationChange = () => {
    setKey((prevKey) => prevKey + 1)
  }

  useEffect(() => {
    window.addEventListener('resize', handleRotationChange)

    return () => {
      window.removeEventListener('resize', handleRotationChange)
    }
  }, [])

  useEffect(() => {
    const validData = donorData?.data.filter((d) => d.donationHB)

    const categories = _.map(validData, (d) => {
      if (d.donationHB) {
        return dayjs(d.donationDate).format(dateFormat)
      }
      return false
    }).reverse()

    const getMinValue = _.minBy(validData, (d) => d.donationHB)
    const getMaxValue = _.maxBy(validData, (d) => d.donationHB)
    if (getMaxValue && getMinValue) {
      const minValue = JSON.parse(getMinValue.donationHB)
      const maxValue = JSON.parse(getMaxValue.donationHB)

      if (minValue < chartOptions.yAxis.min) {
        chartOptions.yAxis.min = Math.floor(minValue / 10) * 10
      }

      if (maxValue > chartOptions.yAxis.max) {
        chartOptions.yAxis.max = Math.ceil(maxValue / 10) * 10
      }
    }

    const columns = _.filter(donorData?.columns, (c) => c.in_graph)
    const series = _.map(columns, (c) => {
      const useRandom =
        process.env.NODE_ENV === 'development' && !_.sumBy(validData, (d) => d[c.accessor])

      return {
        name: c.Header,
        data: validData
          .map((data) => {
            if (data[c.accessor]) {
              return JSON.parse(data[c.accessor])
            }
            return useRandom ? _.random(110, 145) : 0
          })
          .reverse(),
        color: '#AF0A3C',
        lineWidth: 4,
        marker: {
          radius: 6,
        },
        tooltip: {
          pointFormatter() {
            return `<strong>${this.series.name}:</strong> ${this.y} ${donationHBUnit || 'g/l'}`
          },
          xDateFormat: '%e %b %Y',
        },
      }
    }).reverse()

    setChartOptions({
      ...chartOptions,
      xAxis: {
        categories,
      },
      series,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [donorData])

  return (
    <div id="donation-graph" className="rounded bg-light overflow-hidden shadow w-100" key={key}>
      <div className="header">
        <span>{__.trans('donation.heading-hb')}</span>
      </div>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </div>
  )
}
